/**
 * Marketing Email Templates
 * Hardcoded templates for marketing campaigns
 * Uses consistent styling system matching send-email function templates
 */

// Shared email styling system
const getEmailStyles = () => `
  body {
    margin: 0;
    padding: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    line-height: 1.6;
    color: #374151;
  }
  .container {
    max-width: 600px;
    margin: 0 auto;
    background-color: #f9fafb;
    padding: 20px;
  }
  .header {
    background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
    color: white;
    padding: 30px 20px;
    text-align: center;
    border-radius: 8px 8px 0 0;
  }
  .header h1 {
    margin: 10px 0 5px 0;
    font-size: 28px;
    font-weight: 700;
  }
  .header-subtitle {
    margin: 0;
    font-size: 16px;
    opacity: 0.95;
  }
  .content {
    background-color: white;
    padding: 30px;
    border-radius: 0 0 8px 8px;
  }
  .card {
    background-color: #f3f4f6;
    border-radius: 8px;
    padding: 20px;
    margin: 20px 0;
    border-left: 4px solid #2563eb;
  }
  .card.success {
    border-left-color: #16a34a;
    background-color: #f0fdf4;
  }
  .card.warning {
    border-left-color: #f59e0b;
    background-color: #fffbeb;
  }
  .card.info {
    border-left-color: #2563eb;
    background-color: #dbeafe;
  }
  .button {
    display: inline-block;
    background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
    color: white;
    padding: 12px 30px;
    text-decoration: none;
    border-radius: 6px;
    font-weight: 600;
    margin-top: 20px;
    transition: opacity 0.3s;
  }
  .button:hover {
    opacity: 0.9;
  }
  .stats-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
    margin: 20px 0;
  }
  .stat-box {
    text-align: center;
    padding: 20px;
    background-color: #f3f4f6;
    border-radius: 8px;
  }
  .stat-number {
    font-size: 36px;
    font-weight: bold;
    color: #2563eb;
    margin: 0;
  }
  .stat-label {
    color: #6b7280;
    font-size: 14px;
    margin-top: 5px;
  }
  .tips {
    background-color: #f0fdf4;
    border-left: 4px solid #16a34a;
    padding: 20px;
    border-radius: 6px;
    margin: 20px 0;
  }
  .tips h3 {
    color: #16a34a;
    margin-top: 0;
  }
  .tips ul {
    margin: 10px 0;
    padding-left: 20px;
  }
  .tips li {
    margin: 8px 0;
    color: #374151;
  }
  .footer {
    text-align: center;
    padding: 20px 30px;
    background-color: #f9fafb;
    border-radius: 0 0 8px 8px;
    font-size: 13px;
    color: #6b7280;
  }
  .footer a {
    color: #2563eb;
    text-decoration: none;
  }
  .footer p {
    margin: 5px 0;
  }
  a {
    color: #2563eb;
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
  h2 {
    color: #1f2937;
    margin-bottom: 15px;
  }
  h3 {
    color: #374151;
  }
`;

const generateEmailHeader = (title, subtitle = '', logoUrl = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"%3E%3Crect fill="%232563eb" width="40" height="40" rx="8"/%3E%3Ctext x="50%25" y="50%25" font-size="20" font-weight="bold" fill="white" text-anchor="middle" dy=".3em"%3EVA%3C/text%3E%3C/svg%3E') => `
  <div class="header">
    <img src="${logoUrl}" alt="VA Sprouts" style="width: 60px; height: 60px; margin-bottom: 10px; border-radius: 8px;">
    <h1>${title}</h1>
    ${subtitle ? `<p class="header-subtitle">${subtitle}</p>` : ''}
  </div>
`;

export const EMAIL_TEMPLATES = {
  WELCOME_PROMOTION: {
    id: 'welcome_promotion',
    name: 'Welcome Promotion',
    subject: '✨ Welcome to VA Sprouts - Special Offer Inside!',
    description: 'Welcome new users with a special promotion',
    variables: ['firstName', 'offerDetails', 'expiryDate', 'appUrl'],
    htmlTemplate: `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Welcome to VA Sprouts</title>
  <style>${getEmailStyles()}</style>
</head>
<body>
  <div class="container">
    ${generateEmailHeader('Welcome to VA Sprouts!', 'Get started with an exclusive offer')}
    
    <div class="content">
      <h2>Hello {{firstName}}! 🎉</h2>
      <p>We're thrilled to have you join our community of talented Virtual Assistants and forward-thinking employers.</p>
      
      <div class="card success">
        <h3 style="margin-top: 0; color: #16a34a;">🎁 Special Welcome Offer</h3>
        <p>{{offerDetails}}</p>
        <p style="color: #6b7280; font-size: 14px; margin-top: 10px; margin-bottom: 0;">
          ⏰ Offer expires: {{expiryDate}}
        </p>
      </div>
      
      <p>Start exploring opportunities and connect with professionals who can help grow your career or business.</p>
      
      <div style="text-align: center;">
        <a href="{{appUrl}}" class="button">Get Started</a>
      </div>
      
      <p style="color: #6b7280; font-size: 14px; margin-top: 30px;">
        Questions? Reply to this email or visit our <a href="{{appUrl}}/support">support center</a>.
      </p>
    </div>
    
    <div class="footer">
      <p>© ${new Date().getFullYear()} VA Sprouts. All rights reserved.</p>
      <p>Questions? <a href="mailto:support@vasprouts.com">Contact us</a></p>
    </div>
  </div>
</body>
</html>`
  },
  
  NEW_FEATURES: {
    id: 'new_features',
    name: 'New Features Announcement',
    subject: '🚀 Exciting New Features on VA Sprouts!',
    description: 'Announce new platform features and improvements',
    variables: ['firstName', 'featureTitle', 'featureDescription', 'ctaText', 'ctaUrl', 'appUrl'],
    htmlTemplate: `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>New Features</title>
  <style>${getEmailStyles()}</style>
</head>
<body>
  <div class="container">
    ${generateEmailHeader('New Features Just for You!', 'We\'ve improved your experience')}
    
    <div class="content">
      <h2>Hello {{firstName}}!</h2>
      <p>We've been working hard to improve your VA Sprouts experience. Check out what's new:</p>
      
      <div class="card info">
        <h3 style="margin-top: 0; color: #1e40af;">✨ {{featureTitle}}</h3>
        <p>{{featureDescription}}</p>
      </div>
      
      <div style="text-align: center;">
        <a href="{{ctaUrl}}" class="button">{{ctaText}}</a>
      </div>
      
      <p style="color: #6b7280; font-size: 14px; margin-top: 30px;">
        We'd love to hear your feedback! Reply to this email with your thoughts.
      </p>
    </div>
    
    <div class="footer">
      <p>© ${new Date().getFullYear()} VA Sprouts. All rights reserved.</p>
      <p>Questions? <a href="mailto:support@vasprouts.com">Contact us</a></p>
    </div>
  </div>
</body>
</html>`
  },
  
  MONTHLY_DIGEST: {
    id: 'monthly_digest',
    name: 'Monthly Platform Digest',
    subject: '📊 Your Monthly VA Sprouts Update',
    description: 'Monthly summary of platform activity and opportunities',
    variables: ['firstName', 'newJobsCount', 'profileViewsCount', 'platformUpdate', 'appUrl'],
    htmlTemplate: `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Monthly Update</title>
  <style>${getEmailStyles()}</style>
</head>
<body>
  <div class="container">
    ${generateEmailHeader('Your Monthly Update', 'Here\'s what happened this month')}
    
    <div class="content">
      <h2>Hello {{firstName}}!</h2>
      <p>Here's a summary of your activity on VA Sprouts this month:</p>
      
      <div class="stats-grid">
        <div class="stat-box" style="background-color: #dbeafe; border-left: 4px solid #2563eb;">
          <p class="stat-number">{{newJobsCount}}</p>
          <p class="stat-label">New Job Postings</p>
        </div>
        <div class="stat-box" style="background-color: #fef3c7; border-left: 4px solid #f59e0b;">
          <p class="stat-number" style="color: #f59e0b;">{{profileViewsCount}}</p>
          <p class="stat-label">Profile Views</p>
        </div>
      </div>
      
      <div class="card info">
        <h3 style="margin-top: 0;">📌 Platform Update</h3>
        <p>{{platformUpdate}}</p>
      </div>
      
      <div style="text-align: center;">
        <a href="{{appUrl}}/job-search" class="button">Browse Jobs</a>
      </div>
      
      <p style="color: #6b7280; font-size: 14px; margin-top: 30px;">
        Stay active on VA Sprouts to maximize your opportunities!
      </p>
    </div>
    
    <div class="footer">
      <p>© ${new Date().getFullYear()} VA Sprouts. All rights reserved.</p>
      <p>Questions? <a href="mailto:support@vasprouts.com">Contact us</a></p>
    </div>
  </div>
</body>
</html>`
  },
  
  SPECIAL_PROMOTION: {
    id: 'special_promotion',
    name: 'Special Promotion',
    subject: '⏰ Limited Time Offer - Don\'t Miss Out!',
    description: 'Time-sensitive promotional campaigns',
    variables: ['firstName', 'promotionTitle', 'promotionDetails', 'discountCode', 'expiryDate', 'appUrl'],
    htmlTemplate: `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Special Promotion</title>
  <style>${getEmailStyles()}</style>
</head>
<body>
  <div class="container">
    <div style="background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); color: white; padding: 30px 20px; text-align: center; border-radius: 8px 8px 0 0;">
      <div style="background: rgba(0,0,0,0.1); padding: 10px 20px; border-radius: 6px; display: inline-block; margin-bottom: 15px; font-weight: bold;">
        ⏰ LIMITED TIME OFFER
      </div>
      <h1 style="margin: 10px 0 5px 0; font-size: 28px; font-weight: 700;">{{promotionTitle}}</h1>
      <p style="margin: 0; font-size: 16px; opacity: 0.95;">Don't miss this exclusive opportunity!</p>
    </div>
    
    <div class="content">
      <h2>Hello {{firstName}}!</h2>
      <p>{{promotionDetails}}</p>
      
      <div style="background-color: #fef3c7; border: 3px dashed #f59e0b; padding: 25px; border-radius: 8px; margin: 25px 0; text-align: center;">
        <div style="color: #92400e; font-size: 14px; margin-bottom: 8px; font-weight: 600;">💳 USE THIS CODE:</div>
        <div style="font-size: 32px; font-weight: bold; color: #f59e0b; letter-spacing: 3px; font-family: monospace;">{{discountCode}}</div>
        <p style="color: #92400e; margin: 10px 0 0 0; font-size: 13px;">Copy this code at checkout</p>
      </div>
      
      <div class="card warning">
        <p style="margin: 0; font-weight: bold; color: #991b1b;">
          ⏱️ Offer expires: <strong>{{expiryDate}}</strong>
        </p>
      </div>
      
      <div style="text-align: center;">
        <a href="{{appUrl}}" style="display: inline-block; background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); color: white; padding: 14px 40px; text-decoration: none; border-radius: 6px; font-weight: 600; margin-top: 20px; font-size: 16px;">Claim Offer Now</a>
      </div>
      
      <p style="color: #6b7280; font-size: 13px; margin-top: 30px; line-height: 1.6;">
        <em>* Terms and conditions apply. This offer cannot be combined with other promotions. Limited to new and eligible existing customers.</em>
      </p>
    </div>
    
    <div class="footer">
      <p>© ${new Date().getFullYear()} VA Sprouts. All rights reserved.</p>
      <p>Questions? <a href="mailto:support@vasprouts.com">Contact us</a></p>
    </div>
  </div>
</body>
</html>`
  },
  
  CUSTOM: {
    id: 'custom',
    name: 'Custom Email',
    subject: 'VA Sprouts Update',
    description: 'Fully customizable email template',
    variables: ['firstName', 'customContent', 'appUrl'],
    htmlTemplate: `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>VA Sprouts Update</title>
  <style>${getEmailStyles()}</style>
</head>
<body>
  <div class="container">
    ${generateEmailHeader('VA Sprouts Update', 'We have an important message for you')}
    
    <div class="content">
      <h2>Hello {{firstName}}!</h2>
      
      <div style="color: #374151; font-size: 16px; line-height: 1.8; margin: 20px 0;">
        {{customContent}}
      </div>
      
      <div style="text-align: center;">
        <a href="{{appUrl}}" class="button">Visit VA Sprouts</a>
      </div>
      
      <p style="color: #6b7280; font-size: 14px; margin-top: 30px;">
        Best regards,<br>
        <strong>The VA Sprouts Team</strong>
      </p>
    </div>
    
    <div class="footer">
      <p>© ${new Date().getFullYear()} VA Sprouts. All rights reserved.</p>
      <p>Questions? <a href="mailto:support@vasprouts.com">Contact us</a></p>
    </div>
  </div>
</body>
</html>`
  }
};

/**
 * Replace template variables with actual values
 * @param {string} template - Template string with {{variable}} placeholders
 * @param {object} variables - Object with variable values
 * @returns {string} - Template with replaced variables
 */
export const replaceTemplateVariables = (template, variables) => {
  if (!template || typeof template !== 'string') {
    console.error('Invalid template provided to replaceTemplateVariables');
    return '';
  }
  
  let result = template;
  
  Object.keys(variables).forEach(key => {
    const value = variables[key];
    // Only replace if value exists and can be converted to string
    if (value !== undefined && value !== null) {
      const regex = new RegExp(`{{${key}}}`, 'g');
      result = result.replace(regex, String(value));
    }
  });
  
  return result;
};

/**
 * Get template by ID
 * @param {string} templateId - Template identifier
 * @returns {object|null} - Template object or null if not found
 */
export const getTemplateById = (templateId) => {
  return Object.values(EMAIL_TEMPLATES).find(t => t.id === templateId) || null;
};

/**
 * Get all available templates
 * @returns {array} - Array of template objects
 */
export const getAllTemplates = () => {
  return Object.values(EMAIL_TEMPLATES);
};
